import {
  Box,
  Button,
  Image,
  Flex,
  Text,
  Heading,
  Icon,
  Stack,
  HStack,
  ListItem,
  List,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import ComplianceBanner from "components/compliance-banner";
import { FeaturesB2B } from "components/features-b2b";
import { LandingPageProps } from "components/landing/landing-page";
import { PricingCard } from "components/pricing-card";
import { partnerMarketingIncludeList } from "configuration/promotion/discount-locations";
import { IpLocationContext } from "hooks/use-location-detector";
import { LandingLocale } from "localization/landing";
import { GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useContext, useMemo } from "react";
import { FiCheck } from "react-icons/fi";
import { ImQuotesLeft, ImQuotesRight } from "react-icons/im";
import { localeFromLangCode } from "utils/locale-from-header";
import { Section } from "../../components/section";
interface Props {
  l: LandingLocale;
}

export const getStaticProps: GetStaticProps<LandingPageProps> = async (props) => {
  const l = localeFromLangCode(props.params?.lang as string);

  return {
    props: {
      ...l,
    },
    revalidate: 86400, // 24 hours
  };
};

export default function Schools({ l }: Props) {
  const router = useRouter();
  const ipLocation = useContext(IpLocationContext);

  const onStartTrial = () => {
    router.push("/authentication/signup-b2b?forwardSignUp=true");
  };

  const selfServeStatus = useMemo(() => {
    if (ipLocation.isLoading) {
      return "loading";
    }

    if (partnerMarketingIncludeList.includes(ipLocation?.countryCode ?? "")) {
      return "visible";
    }

    return "hidden";
  }, [ipLocation.isLoading, ipLocation?.countryCode]);

  const hideSelfServe = selfServeStatus === "hidden" || selfServeStatus === "loading";

  return (
    <>
      <Head>
        <title>Talkio for Schools</title>
        <meta property="og:title" content={"Talkio for Schools"} key="title" />
      </Head>

      <Box>
        <Section>
          <Flex flexDir="column" flex="1">
            <Heading
              as="h1"
              color={"brand.gray"}
              fontSize={"6xl"}
              fontWeight="bold"
              lineHeight={"4rem"}
              letterSpacing={".001rem"}
              mt={{ base: "30px", lg: "80px", xl: "70px" }}
            >
              Talkio for Schools
            </Heading>
            <Text mt={4}>
              Accelerate your {`students'`}{" "}
              <Box as="span" color="brand.tertiary.main">
                language speaking skills
              </Box>{" "}
              with Talkio for Schools
            </Text>

            <List my={8}>
              {[
                "All the features of Talkio tailored for your school",
                "Multi-user management",
                "Custom content that fits your curriculum",
                "Integrate with your online and offline courses",
                "AI tutors tailored to your teaching style",
                "Student progress tracking",
              ].map((feature, i) => (
                <ListItem key={i}>
                  <HStack spacing="4">
                    <Icon as={FiCheck} color="brand.green.400" boxSize="6" />
                    <Text fontSize="sm" textAlign="left">
                      {feature}
                    </Text>
                  </HStack>
                </ListItem>
              ))}
            </List>

            {selfServeStatus === "hidden" ? (
              <Button
                colorScheme={"brand.primary"}
                color={"brand.gray"}
                maxW={{ base: "100%", sm: "300px" }}
                mb={12}
                onClick={() => {
                  router.push("/partners/schools-contact");
                }}
              >
                {"Let's talk"}
              </Button>
            ) : (
              <Button
                colorScheme={"brand.primary"}
                color={"brand.gray"}
                maxW={{ base: "100%", sm: "300px" }}
                mb={12}
                onClick={onStartTrial}
                isLoading={selfServeStatus === "loading"}
              >
                Start Your 14 Day Free Trial
              </Button>
            )}
          </Flex>
          <Flex display={{ base: "none", md: "flex" }} flex="1" justifyContent={{ base: "center" }}>
            <Image
              src="/images/talkio-schools-2.webp"
              alt="Landing"
              alignSelf={"center"}
              mx={2}
              width={{ base: "250px", sm: "350px", md: "450px" }}
            />
          </Flex>
        </Section>
      </Box>

      <Box backgroundColor="gray.700">
        <Section>
          <Flex my={12} flexDir={"column"} flex="1" color="#fff">
            <Text mx="auto" maxW="600px" textAlign={"center"} fontSize="md">
              In many classrooms, you, the teacher, are the primary source of native language
              exposure for your students. Engaging in daily speaking practice with each student
              would be beneficial but time consuming.
            </Text>
            <Text textAlign={"center"} fontSize="xl" mt={7} fontWeight="bold">
              But what if {`there's`} a better way to master speaking skills?
            </Text>
          </Flex>
        </Section>
      </Box>

      <Section>
        <Flex flexDir={"column"} flex="1" justifyContent={"center"}>
          <Heading
            color={"brand.gray"}
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            lineHeight={1}
            my={5}
          >
            Introducing Talkio for Schools
          </Heading>
          <Text mt="4">
            Talkio enables your students to enhance their speaking skills through{" "}
            <strong>conversations with an AI tutor</strong>, simulating interactions with a native
            speaker. Students receive targeted feedback on pronunciation, sentence structure, and
            mistakes, allowing for a tailored learning experience. This approach allows you to
            concentrate on teaching language fundamentals in the classroom, while students can
            independently <strong>practice speaking</strong> in their own time and space.
          </Text>
        </Flex>
        <Flex flex="1" justifyContent={"center"}>
          <Image
            src="/images/talkio-schools-1.webp"
            alt="Woman speaking with AI"
            height={"auto"}
            alignSelf={"center"}
            width={{ base: "250px", sm: "350px", md: "450px", lg: "100%" }}
          />
        </Flex>
      </Section>

      <Flex
        backgroundColor="gray.700"
        color="#fff"
        textAlign={"center"}
        p={12}
        justifyContent={"center"}
      >
        <Icon as={ImQuotesLeft} fontSize="2xl" mr="5" color="brand.gray.300" />
        <Text mx={4} fontSize="lg">
          Learning <strong>to speak</strong> a new language requires regular practice and
          repetition. <br /> The most effective practice comes from conversations with a native
          speaker.
        </Text>
        <Icon
          alignSelf={"flex-end"}
          as={ImQuotesRight}
          fontSize="2xl"
          mr="5"
          color="brand.gray.300"
        />
      </Flex>

      <Box id="feature-v1" backgroundColor="gray.100">
        <FeaturesB2B l={l} />
        <Divider />
      </Box>

      <Box id="compliance-banner" bg="white">
        <ComplianceBanner />
      </Box>

      <Box backgroundColor="gray.100" py="12">
        <Stack spacing={{ base: "4", md: "6" }} alignItems="center">
          <Stack spacing="3" textAlign={"center"}>
            <Heading size={{ base: "md", md: "lg" }}>Get Started Today</Heading>
          </Stack>
          <Text
            fontSize={{ base: "md" }}
            color="fg.muted"
            maxW="2xl"
            textAlign={"center"}
            id="pricing"
            mb={8}
          >
            We help schools, universities, and tutors worldwide offer a world-class language
            learning experience for their students. Get started with Talkio for Schools today.
          </Text>
        </Stack>

        <Flex
          as="section"
          flexDir={{ base: "column", md: "row" }}
          gap={4}
          alignItems="center"
          justifyContent="center"
          id="pricing"
        >
          {hideSelfServe ? null : (
            <PricingCard
              title="Schools & Private Tutors"
              description="Per student per month"
              price="$7"
              features={[
                "All the features of Talkio for your students",
                "Multi-user management",
                "Customize content to fit your curriculum",
                "Create your own AI Tutors",
                "Student progress tracking",
              ]}
              submitText="Start Your 14 Day Free Trial"
              submitLink="#"
              colorScheme="white"
              productId="prod_PZ000000000000"
              buttonCallback={() => {
                onStartTrial();
              }}
            />
          )}

          <PricingCard
            title="Institutions & Service Providers"
            description="Speak to our team for a custom quote"
            price="Let's talk"
            features={[
              hideSelfServe
                ? "All the features of Talkio for your students"
                : "Everything in for Schools & Private Tutors",
              "White labelling and custom branding",
              "API access and LMS integration",
              "Bulk discounts available",
              "Priority support",
            ]}
            submitText="Book a Demo"
            submitLink="#"
            colorScheme="white"
            productId="prod_PZ000000000000"
            buttonCallback={() => {
              router.push("/partners/schools-contact");
            }}
          />
        </Flex>
      </Box>
    </>
  );
}
